<template>
  <div class="floating-displays-bar fixed bottom-4 right-4 z-50">
    <div class="flex flex-col-reverse items-end gap-3">
      <!-- Displays Container - stacks upward -->
      <div class="displays-container flex flex-col-reverse items-end gap-3">
        <!-- Empty State -->
        <div
          v-if="displaysStore.activeDisplays.length === 0"
          class="flex items-center gap-2 px-4 py-3 rounded-xl bg-background/80 backdrop-blur-sm border border-border shadow-lg text-sm text-muted-foreground"
        >
          <Monitor class="h-4 w-4" />
          <span>No displays</span>
        </div>

        <!-- Display Items - stacking upward -->
        <div
          v-for="display in displaysStore.activeDisplays"
          :key="display.id"
          class="display-item flex flex-col items-center gap-1.5 cursor-pointer group"
          @click="openDisplayEditor(display)"
        >
          <!-- Preview -->
          <div 
            class="relative rounded-lg overflow-hidden border-2 border-border/50 group-hover:border-primary/70 transition-all shadow-lg group-hover:shadow-xl bg-black"
            :style="{
              width: getPreviewWidth(display) + 'px',
              height: getPreviewHeight(display) + 'px'
            }"
          >
            <SceneDisplayConfig
              v-if="currentSceneId"
              :scene-id="currentSceneId"
              :display="display"
              :preview-max-height="getPreviewHeight(display)"
              :preview-max-width="getPreviewWidth(display)"
              :interactive="false"
            />
            <div v-else class="w-full h-full bg-muted/30 flex items-center justify-center">
              <Monitor class="h-8 w-8 text-muted-foreground/30" />
            </div>
            
            <!-- View Button Overlay -->
            <Button
              variant="secondary"
              size="icon"
              class="absolute bottom-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
              @click.stop="openPresentView(display)"
              title="Open in new window"
            >
              <ExternalLink class="h-3.5 w-3.5" />
            </Button>
          </div>
          
          <!-- Display Name -->
          <span class="text-xs text-muted-foreground group-hover:text-foreground transition-colors truncate max-w-[140px]">
            {{ display.name }}
          </span>
        </div>
        
        <!-- Settings Gear (Manage Displays) - appears above displays -->
        <Button
          variant="outline"
          size="icon"
          class="h-10 w-10 rounded-full bg-background/90 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all"
          @click="dialogs.manageDisplays.open()"
          title="Manage displays"
        >
          <Settings class="h-4 w-4" />
        </Button>
      </div>
    </div>

    <!-- Create Display Dialog -->
    <Dialog v-model:open="dialogs.createDisplay.isOpen.value">
      <DialogContent class="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add New Display</DialogTitle>
          <DialogDescription>
            Create a new display that can be configured for different scenes.
          </DialogDescription>
        </DialogHeader>
        
        <DisplayForm
          mode="create"
          @success="handleDisplayCreated"
          @cancel="dialogs.createDisplay.close()"
        />
      </DialogContent>
    </Dialog>

    <!-- Manage Displays Dialog -->
    <Dialog v-model:open="dialogs.manageDisplays.isOpen.value">
      <DialogContent class="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>Manage Displays</DialogTitle>
          <DialogDescription>
            Configure your displays, set their resolution and settings.
          </DialogDescription>
        </DialogHeader>
        
        <div class="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          <div
            v-for="display in displaysStore.activeDisplays"
            :key="display.id"
            class="p-4 rounded-lg border border-border space-y-3"
          >
            <!-- Header row with name and actions -->
            <div class="flex items-center gap-3">
              <Monitor class="h-5 w-5 text-muted-foreground flex-shrink-0" />
              <Input
                :model-value="display.name"
                @update:model-value="(val) => updateDisplayName(display.id, String(val))"
                @blur="saveDisplayName(display)"
                @keydown.enter="($event.target as HTMLInputElement)?.blur()"
                class="h-8 text-sm font-medium flex-1"
                placeholder="Display name"
              />
              <Button
                variant="ghost"
                size="icon"
                class="h-8 w-8 flex-shrink-0"
                @click="openPresentView(display)"
                title="Open in new window"
              >
                <ExternalLink class="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                class="h-8 w-8 flex-shrink-0 text-destructive hover:text-destructive"
                @click="confirmDeleteDisplay(display)"
                title="Delete display"
              >
                <Trash2 class="h-4 w-4" />
              </Button>
            </div>
            
            <!-- Dimensions row -->
            <div class="flex items-center gap-3 pl-8">
              <div class="flex items-center gap-2">
                <Label class="text-xs text-muted-foreground w-10">Width</Label>
                <Input
                  type="number"
                  :model-value="display.width"
                  @update:model-value="(val) => updateDisplayDimension(display.id, 'width', Number(val))"
                  @blur="saveDisplayDimensions(display)"
                  @keydown.enter="($event.target as HTMLInputElement)?.blur()"
                  class="h-7 w-20 text-xs hide-spinners"
                  min="100"
                  max="4000"
                />
              </div>
              <span class="text-muted-foreground">x</span>
              <div class="flex items-center gap-2">
                <Label class="text-xs text-muted-foreground w-10">Height</Label>
                <Input
                  type="number"
                  :model-value="display.height"
                  @update:model-value="(val) => updateDisplayDimension(display.id, 'height', Number(val))"
                  @blur="saveDisplayDimensions(display)"
                  @keydown.enter="($event.target as HTMLInputElement)?.blur()"
                  class="h-7 w-20 text-xs hide-spinners"
                  min="100"
                  max="4000"
                />
              </div>
            </div>
            
            <!-- Actual Screen Size row -->
            <div class="flex flex-col gap-1 pl-8">
              <template v-if="getActualScreenSizes(display.id).length > 0">
                <div 
                  v-for="screenSize in getActualScreenSizes(display.id)"
                  :key="screenSize.instanceId"
                  class="flex items-center gap-2 text-xs"
                >
                  <span class="text-muted-foreground">Actual:</span>
                  <span class="font-mono text-green-600 dark:text-green-400">
                    {{ screenSize.width }} x {{ screenSize.height }}
                  </span>
                  <span class="text-muted-foreground/50 text-[10px]">({{ screenSize.instanceId }})</span>
                </div>
              </template>
              <div 
                v-else
                class="flex items-center gap-2 text-xs text-muted-foreground/60"
              >
                <span>Actual: Not active</span>
              </div>
            </div>
          </div>
          
          <Button
            variant="outline"
            class="w-full"
            @click="openCreateDisplayDialog()"
          >
            <Plus class="h-4 w-4 mr-2" />
            Add Display
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    <!-- Display Editor Modal -->
    <DisplayEditorModal
      v-if="currentSceneId && selectedDisplayForEditor"
      :scene-id="currentSceneId"
      :display="selectedDisplayForEditor"
      :open="dialogs.displayEditor.isOpen.value"
      @update:open="dialogs.displayEditor.isOpen.value = $event"
      @display-updated="selectedDisplayForEditor = $event"
    />

    <!-- Delete Confirmation Dialog -->
    <Dialog v-model:open="dialogs.deleteConfirm.isOpen.value">
      <DialogContent class="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Delete Display</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete "{{ displayToDelete?.name }}"? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div class="flex justify-end gap-2 pt-4">
          <Button variant="outline" @click="dialogs.deleteConfirm.close()">
            Cancel
          </Button>
          <Button variant="destructive" @click="executeDelete" :disabled="isDeleting">
            {{ isDeleting ? 'Deleting...' : 'Delete' }}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Monitor, Plus, ExternalLink, Settings, Trash2 } from 'lucide-vue-next'
import { useDisplaysStore, type Display } from '@/stores/displays'
import { useSceneDisplayConfigsStore } from '@/stores/sceneDisplayConfigs'
import { useDisplayScreenSizeStore } from '@/stores/displayScreenSize'
import { useDialogs } from '@/composables/useDialog'
import { useRealtimeSubscriptions } from '@/composables/useRealtimeSubscription'
import { logger } from '@/lib/logger'
import { Platform } from '@/lib/platform'
import SceneDisplayConfig from '@/components/scenes/SceneDisplayConfig.vue'
import DisplayForm from '@/components/scenes/DisplayForm.vue'
import DisplayEditorModal from '@/components/scenes/DisplayEditorModal.vue'

const route = useRoute()
const displaysStore = useDisplaysStore()
const configsStore = useSceneDisplayConfigsStore()
const screenSizeStore = useDisplayScreenSizeStore()

// Larger preview sizes - constrain by height, let width follow aspect ratio
const PREVIEW_MAX_HEIGHT = 100
const PREVIEW_MAX_WIDTH = 180

const getPreviewScale = (display: Display) => {
  if (!display.width || !display.height || display.height <= 0 || display.width <= 0) {
    return 1
  }
  const heightScale = PREVIEW_MAX_HEIGHT / display.height
  const widthScale = PREVIEW_MAX_WIDTH / display.width
  return Math.min(heightScale, widthScale, 1)
}

const getPreviewHeight = (display: Display) => {
  const scale = getPreviewScale(display)
  if (!display.height || display.height <= 0) return PREVIEW_MAX_HEIGHT
  return Math.max(50, Math.round(display.height * scale))
}

const getPreviewWidth = (display: Display) => {
  const scale = getPreviewScale(display)
  if (!display.width || display.width <= 0) return PREVIEW_MAX_WIDTH
  return Math.max(80, Math.round(display.width * scale))
}

const dialogs = useDialogs({
  createDisplay: {},
  displayEditor: {},
  manageDisplays: {},
  deleteConfirm: {}
})

const selectedDisplayForEditor = ref<Display | null>(null)
const displayToDelete = ref<Display | null>(null)
const isDeleting = ref(false)

// Pending name changes (for optimistic UI)
const pendingNames = ref<Map<string, string>>(new Map())
const pendingDimensions = ref<Map<string, { width?: number; height?: number }>>(new Map())

const currentSceneId = computed(() => {
  return route.params.sceneId as string | undefined
})

// Real-time subscriptions
useRealtimeSubscriptions({
  displays: {
    subscribe: () => displaysStore.subscribeToDisplayUpdates(),
    unsubscribe: (sub) => displaysStore.unsubscribeFromUpdates(sub as any)
  },
  configs: {
    subscribe: () => configsStore.subscribeToConfigUpdates(),
    unsubscribe: (sub) => configsStore.unsubscribeFromUpdates(sub as any)
  }
})

const openDisplayEditor = (display: Display) => {
  selectedDisplayForEditor.value = display
  dialogs.displayEditor.open()
}

const openPresentView = async (display: Display) => {
  const url = `/present/${display.id}`
  
  logger.info('🖥️ [FloatingDisplaysBar] Opening present view:', {
    displayId: display.id,
    displayName: display.name,
    url,
    isDesktop: Platform.isDesktop(),
    platform: Platform.getPlatformName()
  })
  
  if (Platform.isDesktop()) {
    // In Tauri, create a new window using WebviewWindow API
    try {
      logger.info('🖥️ [FloatingDisplaysBar] Attempting to create Tauri window...')
      const { WebviewWindow } = await import('@tauri-apps/api/webviewWindow')
      const uniqueLabel = `present-${display.id}-${Date.now()}`
      
      logger.info('🖥️ [FloatingDisplaysBar] Creating WebviewWindow with label:', uniqueLabel)
      
      const webview = new WebviewWindow(uniqueLabel, {
        url,
        title: `Present: ${display.name}`,
        width: display.width || 1920,
        height: display.height || 1080,
        center: true,
        resizable: true,
        fullscreen: false
      })
      
      webview.once('tauri://created', () => {
        logger.info('🖥️ [FloatingDisplaysBar] Tauri window created successfully:', uniqueLabel)
      })
      
      webview.once('tauri://error', (e) => {
        logger.error('🖥️ [FloatingDisplaysBar] Failed to create presentation window:', e)
      })
    } catch (err) {
      logger.error('🖥️ [FloatingDisplaysBar] Failed to open Tauri window:', err)
      // Fallback to window.open
      logger.info('🖥️ [FloatingDisplaysBar] Falling back to window.open')
      window.open(url, '_blank')
    }
  } else {
    // In web browser, open in new tab
    logger.info('🖥️ [FloatingDisplaysBar] Opening in browser tab')
    window.open(url, '_blank')
  }
}

const updateDisplayName = (displayId: string, newName: string) => {
  pendingNames.value.set(displayId, newName)
}

const saveDisplayName = async (display: Display) => {
  const newName = pendingNames.value.get(display.id)
  if (newName !== undefined && newName !== display.name && newName.trim()) {
    try {
      await displaysStore.updateDisplayById(display.id, { name: newName.trim() })
      pendingNames.value.delete(display.id)
    } catch (error) {
      logger.error('Failed to update display name:', error)
    }
  } else {
    pendingNames.value.delete(display.id)
  }
}

const updateDisplayDimension = (displayId: string, dimension: 'width' | 'height', value: number) => {
  const existing = pendingDimensions.value.get(displayId) || {}
  pendingDimensions.value.set(displayId, { ...existing, [dimension]: value })
}

const saveDisplayDimensions = async (display: Display) => {
  const pending = pendingDimensions.value.get(display.id)
  if (!pending) return
  
  const updates: { width?: number; height?: number } = {}
  if (pending.width !== undefined && pending.width !== display.width && pending.width >= 100) {
    updates.width = pending.width
  }
  if (pending.height !== undefined && pending.height !== display.height && pending.height >= 100) {
    updates.height = pending.height
  }
  
  if (Object.keys(updates).length > 0) {
    try {
      await displaysStore.updateDisplayById(display.id, updates)
    } catch (error) {
      logger.error('Failed to update display dimensions:', error)
    }
  }
  pendingDimensions.value.delete(display.id)
}

const confirmDeleteDisplay = (display: Display) => {
  displayToDelete.value = display
  dialogs.deleteConfirm.open()
}

const executeDelete = async () => {
  if (!displayToDelete.value) return
  
  isDeleting.value = true
  try {
    await displaysStore.deleteDisplay(displayToDelete.value.id)
    dialogs.deleteConfirm.close()
    displayToDelete.value = null
  } catch (error) {
    logger.error('Failed to delete display:', error)
  } finally {
    isDeleting.value = false
  }
}

const openCreateDisplayDialog = () => {
  // Close manage displays first, then open create dialog
  dialogs.manageDisplays.close()
  // Use nextTick to ensure the manage dialog is closed before opening create
  setTimeout(() => {
    dialogs.createDisplay.open()
  }, 100)
}

const handleDisplayCreated = () => {
  dialogs.createDisplay.close()
  // Re-open manage displays after creating
  dialogs.manageDisplays.open()
}

watch(currentSceneId, async (newSceneId) => {
  if (newSceneId) {
    await configsStore.fetchConfigsForScene(newSceneId)
  }
}, { immediate: true })

// Subscribe to screen sizes when manage displays dialog opens
watch(() => dialogs.manageDisplays.isOpen.value, async (isOpen) => {
  if (isOpen) {
    // Subscribe to all display screen sizes
    for (const display of displaysStore.activeDisplays) {
      await screenSizeStore.subscribeToDisplay(display.id)
    }
  } else {
    // Cleanup subscriptions when dialog closes
    await screenSizeStore.cleanupEditor()
  }
})

// Helper to get actual screen sizes for a display (can have multiple instances)
const getActualScreenSizes = (displayId: string) => {
  return screenSizeStore.getScreenSizes(displayId)
}

onMounted(async () => {
  await displaysStore.fetchUserDisplays()
})

onUnmounted(async () => {
  await screenSizeStore.cleanupEditor()
})
</script>

<style scoped>
.displays-container {
  max-width: calc(100vw - 150px);
}

.display-item {
  flex-shrink: 0;
}

/* Hide number input spinners */
.hide-spinners::-webkit-outer-spin-button,
.hide-spinners::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.hide-spinners {
  -moz-appearance: textfield;
}
</style>
